import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputBase,
  Select,
  MenuItem,
  Typography,
  TextField,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import VisibilityIcon from '@material-ui/icons/Visibility';
import SecurityIcon from '@material-ui/icons/Security';
import LockIcon from '@material-ui/icons/Lock';
import LockOpenIcon from '@material-ui/icons/LockOpen';
import WarningIcon from '@material-ui/icons/Warning';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { RootState } from '../../redux/store';
import { fetchCommunityEnrollments, updateCommunityMemberAccess } from '../../redux/actions/communityRolesActions';
import { getUserProgramEnrollmentData } from '../../redux/actions/programEnrollmentAction';
import { getAllPrograms, getProgramById } from '../../redux/actions/programAction';
import { ICommunityRoleUser } from '../../types/communityRoles';
import { usePrivileges } from '../../util/usePrivileges';
import appLogo from '../../assets/logo.png';
import { assetUrl } from '../../util/assetUrl';
import { getChannelMembersApi, updateChannelMembersApi, updateChannelVisibilityApi, saveUserApi } from '../../api';

const invertColor = (hex: string) => {
  let value = (hex || '#ffffff').replace(/^#/, '');
  if (value.length === 3) {
    value = value.split('').map((char) => char + char).join('');
  }
  const num = parseInt(value || 'ffffff', 16);
  const r = 255 - ((num >> 16) & 0xff);
  const g = 255 - ((num >> 8) & 0xff);
  const b = 255 - (num & 0xff);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
};

const contrastColor = (hex: string) => {
  let value = (hex || '').replace('#', '');
  if (value.length === 3) {
    value = value.split('').map((c) => c + c).join('');
  }
  if (!/^[0-9a-fA-F]{6}$/.test(value)) {
    return '#111827';
  }
  const r = parseInt(value.slice(0, 2), 16) / 255;
  const g = parseInt(value.slice(2, 4), 16) / 255;
  const b = parseInt(value.slice(4, 6), 16) / 255;

  const normalize = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  const luminance = 0.2126 * normalize(r) + 0.7152 * normalize(g) + 0.0722 * normalize(b);
  return luminance > 0.55 ? '#111827' : '#F9FAFB';
};

const applyAlpha = (hex: string, alpha: number) => {
  const cleaned = (hex || '#000000').replace('#', '');
  const padded = cleaned.length === 3 ? cleaned.split('').map((c) => c + c).join('') : cleaned;
  const num = parseInt(padded || '000000', 16);
  const r = (num >> 16) & 0xff;
  const g = (num >> 8) & 0xff;
  const b = num & 0xff;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const ASSET_BASE = process.env.REACT_APP_ASSET_BASE_URL || process.env.REACT_APP_API_BASE || 'http://localhost:9090';

const resolveHeaderUrl = (pathValue: string): string => {
  if (!pathValue) return '';
  if (/^(blob:|data:|https?:\/\/)/i.test(pathValue)) return pathValue;
  if (pathValue.startsWith('/assets') || pathValue.startsWith('/uploads')) {
    return `${ASSET_BASE}${pathValue}`;
  }
  try {
    return assetUrl(pathValue);
  } catch (err) {
    return pathValue;
  }
};

const BASE_ROLE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'member', label: 'Member' },
  { value: 'moderator', label: 'Moderator' },
  { value: 'communityadmin', label: 'Community Admin' },
];

const formatRoleLabel = (role: string) => {
  const option = BASE_ROLE_OPTIONS.find((opt) => opt.value === role.toLowerCase());
  return option ? option.label : role;
};

const toDraftRole = (role?: string | null): string => {
  const normalized = (role ?? '').toString().trim().toLowerCase();
  if (normalized === 'communityadmin') return 'communityadmin';
  if (normalized === 'admin') return 'admin';
  if (normalized === 'moderator') return 'moderator';
  return 'member';
};

const fromDraftRole = (draft: string): string => {
  const normalized = (draft ?? '').toString().trim().toLowerCase();
  if (!normalized) return '';
  if (normalized === 'communityadmin') return 'CommunityAdmin';
  if (normalized === 'admin') return 'admin';
  if (normalized === 'moderator') return 'moderator';
  return 'member';
};

const normalizeChannelKey = (value: string) => {
  const parts = (value || '').split(':');
  return parts[parts.length - 1] || value;
};

const normalizeVisibleChannelIds = (values: string[] = []) => {
  const normalized = new Set<string>();
  values.forEach((raw) => {
    normalized.add(normalizeChannelKey(raw));
  });
  return Array.from(normalized.values());
};

const styles = {
  container: {
    minHeight: '100vh',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  } as React.CSSProperties,
  header: {
    padding: '16px 24px',
    borderBottom: '1px solid transparent',
  } as React.CSSProperties,
  mainContent: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '32px 24px',
  },
  searchBox: {
    borderRadius: '12px',
    padding: '12px 16px',
    marginBottom: '24px',
    display: 'flex',
    alignItems: 'center',
    border: '1px solid transparent',
  } as React.CSSProperties,
  memberCard: {
    borderRadius: '16px',
    padding: '20px',
    marginBottom: '12px',
    transition: 'all 0.2s ease',
    border: '1px solid transparent',
  } as React.CSSProperties,
  select: {
    border: '1px solid transparent',
    borderRadius: '12px',
    padding: '10px 16px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    outline: 'none',
    backgroundColor: 'transparent',
  },
  button: {
    borderRadius: '10px',
    textTransform: 'none' as const,
    fontWeight: 600,
    fontSize: '14px',
    padding: '10px 20px',
  },
  channelSearchBox: {
    borderRadius: '10px',
    padding: '10px 14px',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    border: '1px solid transparent',
  },
  channelListContainer: {
    borderRadius: '12px',
    overflow: 'hidden',
    border: '1px solid transparent',
  },
  channelRow: {
    padding: '16px',
    transition: 'all 0.15s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  eyeButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '36px',
    height: '36px',
    borderRadius: '8px',
    transition: 'all 0.15s',
    border: 'none',
    cursor: 'pointer',
  },
  overrideBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 14px',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: 600,
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.15s',
    minWidth: '100px',
    justifyContent: 'center',
  },
  lockButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '36px',
    height: '36px',
    borderRadius: '8px',
    transition: 'all 0.15s',
    border: 'none',
    cursor: 'pointer',
    marginLeft: '8px',
  },
  toggleGroup: {
    display: 'inline-flex',
    borderRadius: '999px',
    padding: '4px',
  },
  toggleButton: {
    border: 'none',
    outline: 'none',
    borderRadius: '999px',
    padding: '6px 16px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    backgroundColor: 'transparent',
  } as React.CSSProperties,
};

const AdminCommunityMembers: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const { singleProgram, allPrograms } = useSelector((state: RootState) => state.programReducer);
  const { enrollment } = useSelector((state: RootState) => state.programEnrollmentReducer);
  const { user } = useSelector((state: RootState) => state.auth);
  const { enrollments, loading, error, updatingUserId } = useSelector((state: RootState) => state.communityRolesReducer);

  const communities = useMemo(() => singleProgram?.communities || [], [singleProgram]);
  const query = new URLSearchParams(location.search);
  const queryCommunity = query.get('community') || undefined;

  const [selectedCommunity, setSelectedCommunity] = useState<string | undefined>(
    queryCommunity || communities[0]?.communityIdentifier
  );

  const enrollmentCommunityIdentifier = useMemo(() => {
    const raw = enrollment?.communityIdentifier;
    return raw ? raw.toString() : undefined;
  }, [enrollment?.communityIdentifier]);

  const storedCommunityIdentifier = useMemo(() => {
    try {
      const cached = JSON.parse(localStorage.getItem('activeProgramEnrollment') || '{}');
      return cached?.communityIdentifier ? cached.communityIdentifier.toString() : undefined;
    } catch {
      return undefined;
    }
  }, []);
  const [memberSearchTerm, setMemberSearchTerm] = useState('');
  const [memberSearchField, setMemberSearchField] = useState<'both' | 'name' | 'email' | 'phone'>('both');
  const [memberRoleFilter, setMemberRoleFilter] = useState<'all' | 'member' | 'moderator' | 'communityadmin' | 'admin'>('all');
  const [memberPhoneFilter, setMemberPhoneFilter] = useState<'all' | 'with-phone' | 'without-phone'>('all');
  const [memberSort, setMemberSort] = useState<'name' | 'email'>('name');
  const [memberSortDir, setMemberSortDir] = useState<'asc' | 'desc'>('asc');
  const [channelSearchTerm, setChannelSearchTerm] = useState('');
  const [channelPanelSearch, setChannelPanelSearch] = useState('');
  const [channelVisibilityFilter, setChannelVisibilityFilter] = useState<'all' | 'public' | 'private'>('all');
  const [channelSort, setChannelSort] = useState<'name' | 'visibility'>('name');
  const [channelSortDir, setChannelSortDir] = useState<'asc' | 'desc'>('asc');
  const [logoBust, setLogoBust] = useState<number>(0);
  const [viewMode, setViewMode] = useState<'members' | 'channels'>('members');

  const [activeMember, setActiveMember] = useState<ICommunityRoleUser | null>(null);
  const [visibleDraft, setVisibleDraft] = useState<string[]>([]);
  const [overrideDraft, setOverrideDraft] = useState<Record<string, string>>({});
  const [roleDraft, setRoleDraft] = useState<string>('member');
  const [firstNameDraft, setFirstNameDraft] = useState<string>('');
  const [lastNameDraft, setLastNameDraft] = useState<string>('');
  const [phoneNumberDraft, setPhoneNumberDraft] = useState<string>('');
  const [emailDraft, setEmailDraft] = useState<string>('');
  const [moderatorChannelMode, setModeratorChannelMode] = useState<'all' | 'select' | null>(null);
  const [showChannelConfig, setShowChannelConfig] = useState(false);
  const [saving, setSaving] = useState(false);
  const [channelAcl, setChannelAcl] = useState<
    Record<string, { visibility: 'public' | 'private'; allowedUserIds: string[]; createdByUserId?: string }>
  >({});
  const [channelAclLoading, setChannelAclLoading] = useState(false);
  const [channelAclError, setChannelAclError] = useState<string | null>(null);
  const [visibilityUpdating, setVisibilityUpdating] = useState<Record<string, boolean>>({});
  const [channelAclModalOpen, setChannelAclModalOpen] = useState(false);
  const [activeChannelAcl, setActiveChannelAcl] = useState<any>(null);
  const [channelVisibilityDraft, setChannelVisibilityDraft] = useState<'public' | 'private'>('public');
  const [selectedChannelMembers, setSelectedChannelMembers] = useState<string[]>([]);
  const [channelMemberSearch, setChannelMemberSearch] = useState('');
  const [channelAclSaving, setChannelAclSaving] = useState(false);
  const [channelMembersSnapshot, setChannelMembersSnapshot] = useState<string[]>([]);
  const [channelCreatorId, setChannelCreatorId] = useState<string | undefined>(undefined);

  const programId = singleProgram?._id;
  const [selectedProgram, setSelectedProgram] = useState<string | undefined>(programId);

  // Fetch all programs on mount
  useEffect(() => {
    if (!allPrograms || allPrograms.length === 0) {
      dispatch(getAllPrograms() as any);
    }
  }, [dispatch, allPrograms]);

  // Sync selectedProgram with programId when it changes
  useEffect(() => {
    if (programId && !selectedProgram) {
      setSelectedProgram(programId);
    }
  }, [programId, selectedProgram]);

  // Handle program switching
  const handleProgramChange = useCallback((newProgramId: string) => {
    setSelectedProgram(newProgramId);
    dispatch(getProgramById(newProgramId) as any);
    // Reset community selection when switching programs
    setSelectedCommunity(undefined);
  }, [dispatch]);

  useEffect(() => {
    if (selectedCommunity) return;
    const fallback =
      queryCommunity ||
      enrollmentCommunityIdentifier ||
      storedCommunityIdentifier ||
      communities[0]?.communityIdentifier;
    if (fallback) {
      setSelectedCommunity(fallback);
    }
  }, [
    selectedCommunity,
    queryCommunity,
    enrollmentCommunityIdentifier,
    storedCommunityIdentifier,
    communities,
  ]);

  useEffect(() => {
    if (!selectedCommunity && communities.length > 0) return;
    if (!selectedCommunity || communities.length === 0) return;
    const exists = communities.some(
      (community) => community.communityIdentifier === selectedCommunity
    );
    if (exists) return;
    const fallback =
      enrollmentCommunityIdentifier ||
      storedCommunityIdentifier ||
      communities[0]?.communityIdentifier;
    if (fallback) {
      setSelectedCommunity(fallback);
    }
  }, [
    selectedCommunity,
    communities,
    enrollmentCommunityIdentifier,
    storedCommunityIdentifier,
  ]);

  useEffect(() => {
    if (selectedCommunity) {
      const params = new URLSearchParams(location.search);
      params.set('community', selectedCommunity);
      history.replace({ search: params.toString() });
    }
  }, [selectedCommunity, history, location.search]);

  useEffect(() => {
    setChannelAcl({});
    setChannelAclError(null);
  }, [selectedCommunity]);

  useEffect(() => {
    if (!singleProgram?._id || !user?._id) return;
    const matches =
      enrollment &&
      enrollment.programId &&
      String(enrollment.programId) === String(singleProgram._id) &&
      enrollment.userId &&
      String(enrollment.userId) === String(user._id);
    if (!matches) {
      dispatch(getUserProgramEnrollmentData(singleProgram._id, user._id));
    }
  }, [dispatch, enrollment, singleProgram?._id, user?._id]);

  useEffect(() => {
    if (programId && selectedCommunity) {
      dispatch(fetchCommunityEnrollments(programId, selectedCommunity));
    }
  }, [dispatch, programId, selectedCommunity]);

  useEffect(() => {
    setLogoBust(Date.now());
  }, [selectedCommunity]);

  const activeCommunity = useMemo(
    () => communities.find((community) => community.communityIdentifier === selectedCommunity),
    [communities, selectedCommunity]
  );

  const communityTheme = {
    mainColor: activeCommunity?.communityStyle?.mainColor || '#007AFF',
    secondaryColor: activeCommunity?.communityStyle?.secondaryColor || '#ffffff',
    fontStyle: activeCommunity?.communityStyle?.fontStyle || 'Arial',
    logo: activeCommunity?.communityStyle?.logo || '',
  };

  const accentColor = communityTheme.mainColor || '#007AFF';
  const accentForeground = contrastColor(accentColor);
  const secondaryForeground = contrastColor(communityTheme.secondaryColor || '#ffffff');
  const subtleAccentSurface = applyAlpha(accentColor, 0.08);
  const mediumAccentSurface = applyAlpha(accentColor, 0.16);
  const mutedTextColor = applyAlpha(secondaryForeground, 0.6);

  const mapVisibility = useCallback((value: any): 'public' | 'private' => {
    const normalized = (value ?? '').toString().toLowerCase();
    return normalized === 'private' || normalized === 'restricted' ? 'private' : 'public';
  }, []);

  const viewModes: Array<{ value: 'members' | 'channels'; label: string }> = useMemo(
    () => [
      { value: 'members', label: 'Members' },
      { value: 'channels', label: 'Pending Invitations' },
    ],
    []
  );

  const communityLogoUrl = useMemo(() => {
    const resolved = resolveHeaderUrl(communityTheme.logo);
    if (!resolved) return '';
    return logoBust ? `${resolved}${resolved.includes('?') ? '&' : '?'}v=${logoBust}` : resolved;
  }, [communityTheme.logo, logoBust]);

  const pageBackground = communityTheme.secondaryColor || '#f4f5f7';
  const borderSubtle = applyAlpha(secondaryForeground, 0.12);
  const surfaceSoft = applyAlpha(secondaryForeground, 0.08);
  const surfaceStrong = applyAlpha(secondaryForeground, 0.14);
  const toggleBackground = applyAlpha(secondaryForeground, 0.1);
  const buttonHover = applyAlpha(accentColor, 0.2);

  const themedStyles = useMemo(
    () => ({
      container: {
        ...styles.container,
        backgroundColor: pageBackground,
        color: secondaryForeground,
      } as React.CSSProperties,
      header: {
        ...styles.header,
        backgroundColor: pageBackground,
        borderBottom: `1px solid ${borderSubtle}`,
      } as React.CSSProperties,
      mainContent: {
        ...styles.mainContent,
      },
      searchBox: {
        ...styles.searchBox,
        backgroundColor: surfaceSoft,
        border: `1px solid ${borderSubtle}`,
      } as React.CSSProperties,
      memberCard: {
        ...styles.memberCard,
        backgroundColor: '#ffffff',
        border: `1px solid ${borderSubtle}`,
        boxShadow: `0 2px 8px ${applyAlpha(secondaryForeground, 0.18)}`,
      } as React.CSSProperties,
      channelSearchBox: {
        ...styles.channelSearchBox,
        backgroundColor: surfaceSoft,
        border: `1px solid ${borderSubtle}`,
      },
      channelListContainer: {
        ...styles.channelListContainer,
        border: `1px solid ${borderSubtle}`,
        backgroundColor: '#ffffff',
      },
      channelRow: {
        ...styles.channelRow,
        borderBottom: `1px solid ${borderSubtle}`,
      },
      eyeButton: {
        ...styles.eyeButton,
        backgroundColor: surfaceSoft,
      },
      lockButton: {
        ...styles.lockButton,
        backgroundColor: surfaceSoft,
      },
      overrideBadge: {
        ...styles.overrideBadge,
        backgroundColor: surfaceSoft,
      },
      toggleGroup: {
        ...styles.toggleGroup,
        backgroundColor: toggleBackground,
      },
      toggleButton: {
        ...styles.toggleButton,
      },
      select: {
        ...styles.select,
        backgroundColor: '#ffffff',
        border: `1px solid ${borderSubtle}`,
        color: secondaryForeground,
      },
      button: {
        ...styles.button,
      },
      buttonHover,
      surfaceSoft,
      surfaceStrong,
      borderSubtle,
    }),
    [
      pageBackground,
      secondaryForeground,
      borderSubtle,
      surfaceSoft,
      surfaceStrong,
      toggleBackground,
      buttonHover,
    ]
  );

  const handleCloseChannelAcl = useCallback(() => {
    if (channelAclSaving) return;
    setChannelAclModalOpen(false);
    setActiveChannelAcl(null);
    setSelectedChannelMembers([]);
    setChannelMembersSnapshot([]);
    setChannelCreatorId(undefined);
    setChannelMemberSearch('');
  }, [channelAclSaving]);

  useEffect(() => {
    if (channelVisibilityDraft === 'public') {
      if (channelCreatorId) {
        setSelectedChannelMembers([channelCreatorId]);
      } else {
        setSelectedChannelMembers([]);
      }
    } else if (channelVisibilityDraft === 'private' && channelCreatorId) {
      setSelectedChannelMembers((prev) => {
        if (prev.includes(channelCreatorId)) return prev;
        return [...prev, channelCreatorId];
      });
    }
  }, [channelVisibilityDraft, channelCreatorId]);

  const toggleChannelMemberSelection = useCallback(
    (userId: string) => {
      if (channelCreatorId && userId === channelCreatorId) return;
      setSelectedChannelMembers((prev) => {
        if (prev.includes(userId)) {
          return prev.filter((id) => id !== userId);
        }
        return [...prev, userId];
      });
    },
    [channelCreatorId]
  );

  const { privileges, fallbackPlatformAdmin, fallbackCommunityAdmin } = usePrivileges(
    selectedCommunity || undefined
  );

  const normalizedUserType = (user?.type || '').trim().toLowerCase();
  const isTeamAdmin = normalizedUserType === 'team-admin';
  const showCommunitySelector = fallbackPlatformAdmin || privileges.platformAdmin;
  const communityLabel = activeCommunity?.communityName || selectedCommunity || '—';
  const canManageRoles =
    fallbackPlatformAdmin ||
    fallbackCommunityAdmin ||
    privileges.platformAdmin ||
    privileges.isCommunityAdmin(selectedCommunity || undefined) ||
    isTeamAdmin;
  const canManageChannelAcl =
    fallbackPlatformAdmin ||
    fallbackCommunityAdmin ||
    privileges.platformAdmin ||
    privileges.isCommunityAdmin(selectedCommunity || undefined);
  const isAdminLike = fallbackPlatformAdmin || fallbackCommunityAdmin || isTeamAdmin;

  const channelOptions = useMemo(() => activeCommunity?.channels || [], [activeCommunity]);

  const filteredMembers = useMemo(() => {
    const term = memberSearchTerm.trim().toLowerCase();
    const sortMultiplier = memberSortDir === 'asc' ? 1 : -1;

    const getProfile = (member: ICommunityRoleUser) =>
      (member.user && typeof member.user === 'object' ? (member.user as any) : null) as
        | { name?: string; email?: string; phoneNumber?: string }
        | null;

    const getName = (member: ICommunityRoleUser) => {
      const profile = getProfile(member);
      return (profile?.name || profile?.email || member.userId || '').toString();
    };

    const getEmail = (member: ICommunityRoleUser) => {
      const profile = getProfile(member);
      return (profile?.email || '').toString();
    };

    const getPhoneNumber = (member: ICommunityRoleUser) => {
      const profile = getProfile(member);
      return (profile?.phoneNumber || '').toString();
    };

    return enrollments
      .filter((member) => {
        const role = toDraftRole(member.role);
        if (memberRoleFilter !== 'all' && role !== memberRoleFilter) return false;

        // Phone filter
        const memberPhone = getPhoneNumber(member);
        if (memberPhoneFilter === 'with-phone' && !memberPhone) return false;
        if (memberPhoneFilter === 'without-phone' && memberPhone) return false;

        if (!term) return true;

        const name = getName(member).toLowerCase();
        const email = getEmail(member).toLowerCase();
        const phoneNumber = memberPhone.toLowerCase();
        const id = (member.userId || '').toString().toLowerCase();

        if (memberSearchField === 'name') {
          return name.includes(term) || id.includes(term);
        }
        if (memberSearchField === 'email') {
          return email.includes(term) || id.includes(term);
        }
        if (memberSearchField === 'phone') {
          return phoneNumber.includes(term) || id.includes(term);
        }
        return name.includes(term) || email.includes(term) || id.includes(term);
      })
      .sort((a, b) => {
        const aValue = (memberSort === 'name' ? getName(a) : getEmail(a)).toLowerCase();
        const bValue = (memberSort === 'name' ? getName(b) : getEmail(b)).toLowerCase();
        if (aValue !== bValue) {
          return aValue.localeCompare(bValue) * sortMultiplier;
        }
        const aName = getName(a).toLowerCase();
        const bName = getName(b).toLowerCase();
        if (aName !== bName) {
          return aName.localeCompare(bName) * sortMultiplier;
        }
        const aEmail = getEmail(a).toLowerCase();
        const bEmail = getEmail(b).toLowerCase();
        return aEmail.localeCompare(bEmail) * sortMultiplier;
      });
  }, [
    enrollments,
    memberSearchTerm,
    memberSearchField,
    memberRoleFilter,
    memberPhoneFilter,
    memberSort,
    memberSortDir,
  ]);

  const filteredChannels = useMemo(() => {
    if (!channelSearchTerm) return channelOptions;
    const term = channelSearchTerm.toLowerCase();
    return channelOptions.filter((channel: any) =>
      (channel.channelName || channel.channelIdentifier || '').toLowerCase().includes(term)
    );
  }, [channelOptions, channelSearchTerm]);

  const filteredChannelList = useMemo(() => {
    const term = channelPanelSearch.trim().toLowerCase();
    const sortMultiplier = channelSortDir === 'asc' ? 1 : -1;

    const items = (channelOptions || []).map((channel: any) => {
      const id = channel.channelIdentifier;
      const label = (channel.channelName || id || '').toString();
      const aclInfo = channelAcl[id];
      const visibility = aclInfo?.visibility ?? mapVisibility(channel.channelPrivacySettings?.visibility);
      const allowedCount = aclInfo?.allowedUserIds?.length ?? 0;
      return { channel, id, label, aclInfo, visibility, allowedCount };
    });

    return items
      .filter((item) => {
        if (channelVisibilityFilter !== 'all' && item.visibility !== channelVisibilityFilter) {
          return false;
        }
        if (!term) return true;
        return (
          item.label.toLowerCase().includes(term) ||
          item.id.toLowerCase().includes(term)
        );
      })
      .sort((a, b) => {
        if (channelSort === 'name') {
          const result = a.label.toLowerCase().localeCompare(b.label.toLowerCase());
          if (result !== 0) return result * sortMultiplier;
          return a.id.toLowerCase().localeCompare(b.id.toLowerCase()) * sortMultiplier;
        }

        const aValue = a.visibility === 'private' ? 1 : 0;
        const bValue = b.visibility === 'private' ? 1 : 0;
        if (aValue !== bValue) {
          return (aValue - bValue) * sortMultiplier;
        }
        return a.label.toLowerCase().localeCompare(b.label.toLowerCase()) * sortMultiplier;
      });
  }, [
    channelOptions,
    channelPanelSearch,
    channelVisibilityFilter,
    channelSort,
    channelSortDir,
    channelAcl,
    mapVisibility,
  ]);

  const channelMemberOptions = useMemo(() => {
    const term = channelMemberSearch.trim().toLowerCase();
    return enrollments
      .map((member) => {
        const id = member.userId ? member.userId.toString() : '';
        if (!id) return null;
        const profile = member.user && typeof member.user === 'object' ? member.user : null;
        const name = profile?.name || profile?.email || id;
        const email = profile?.email || '';
        const label = name || email || id;
        const role = formatRoleLabel(member.role || 'member');
        return { id, label, email, role };
      })
      .filter((option): option is { id: string; label: string; email: string; role: string } => Boolean(option))
      .filter((option) => {
        if (!term) return true;
        return (
          option.label.toLowerCase().includes(term) ||
          option.email.toLowerCase().includes(term) ||
          option.role.toLowerCase().includes(term)
        );
      });
  }, [enrollments, channelMemberSearch]);

  const normaliseAcl = useCallback(
    (channel: any) => {
      if (!channel) return null;
      const privacy = channel.channelPrivacySettings || {};
      const visibility = mapVisibility(privacy.visibility || privacy.type || 'public');
      const allowedUserIds = Array.isArray(privacy.allowedUserIds)
        ? privacy.allowedUserIds.map((id: any) => (id ?? '').toString()).filter(Boolean)
        : [];
      const createdByUserId = privacy.createdByUserId ? privacy.createdByUserId.toString() : undefined;
      return { visibility, allowedUserIds, createdByUserId };
    },
    [mapVisibility]
  );

  const applyChannelAclUpdate = useCallback(
    (channelId: string, channel: any) => {
      const acl = normaliseAcl(channel);
      if (!acl) return;
      setChannelAcl((prev) => ({ ...prev, [channelId]: acl }));
    },
    [normaliseAcl]
  );

  const getAclInfo = useCallback(
    async (channelId: string) => {
      if (!programId || !selectedCommunity || !canManageChannelAcl) {
        return channelAcl[channelId] || null;
      }

      if (channelAcl[channelId]) {
        return channelAcl[channelId];
      }

      try {
        const response = await getChannelMembersApi(programId, selectedCommunity, channelId);
        applyChannelAclUpdate(channelId, response.channel);
        return normaliseAcl(response.channel);
      } catch (err) {
        console.error('Failed to load channel ACL', err);
        setChannelAclError('Failed to load channel visibility data');
        return channelAcl[channelId] || null;
      }
    },
    [programId, selectedCommunity, canManageChannelAcl, channelAcl, applyChannelAclUpdate, normaliseAcl]
  );

  const handleOpenChannelAcl = useCallback(
    async (channel: any) => {
      try {
        const info = await getAclInfo(channel.channelIdentifier);
        const resolvedVisibility = info?.visibility ?? mapVisibility(channel.channelPrivacySettings?.visibility);
        const allowedIds = info?.allowedUserIds?.map((value: any) => value?.toString?.() ?? value) || [];
        const creator = info?.createdByUserId ? info.createdByUserId.toString() : undefined;
        const initialMembers = resolvedVisibility === 'private'
          ? Array.from(new Set([...allowedIds, creator].filter(Boolean)))
          : [];
        setChannelVisibilityDraft(resolvedVisibility);
        setSelectedChannelMembers(initialMembers);
        setChannelMembersSnapshot(initialMembers);
        setChannelCreatorId(creator);
        setActiveChannelAcl(channel);
        setChannelMemberSearch('');
        setChannelAclModalOpen(true);
        setChannelAclError(null);
      } catch (err) {
        console.error('Failed to load channel ACL', err);
        setChannelAclError('Failed to load channel visibility data');
      }
    },
    [getAclInfo, mapVisibility]
  );

  const ensureChannelAcl = useCallback(
    async (force: boolean = false) => {
      if (!programId || !selectedCommunity || !canManageChannelAcl) return;
      const pending = (channelOptions || [])
        .map((channel: any) => channel?.channelIdentifier)
        .filter((id: string) => id && (force || !channelAcl[id]));
      if (pending.length === 0) return;

      setChannelAclLoading(true);
      setChannelAclError(null);
      try {
        const results = await Promise.all(
          pending.map(async (channelId: string) => {
            try {
              const response = await getChannelMembersApi(programId, selectedCommunity, channelId);
              const acl = normaliseAcl(response.channel);
              return { channelId, acl };
            } catch (err) {
              console.error('Failed to load channel ACL', err);
              return { channelId, acl: null };
            }
          })
        );

        const updates: Record<string, { visibility: 'public' | 'private'; allowedUserIds: string[]; createdByUserId?: string }> = {};
        results.forEach(({ channelId, acl }) => {
          if (acl) {
            updates[channelId] = acl;
          }
        });

        if (Object.keys(updates).length > 0) {
          setChannelAcl((prev) => ({ ...prev, ...updates }));
        }
      } finally {
        setChannelAclLoading(false);
      }
    },
    [programId, selectedCommunity, canManageChannelAcl, channelOptions, channelAcl, normaliseAcl]
  );

  const handleSaveChannelAcl = useCallback(async () => {
    if (!programId || !selectedCommunity || !activeChannelAcl) return;
    setChannelAclSaving(true);
    try {
      const channelId = activeChannelAcl.channelIdentifier;
      const currentInfo = channelAcl[channelId];
      const previousVisibility = currentInfo?.visibility ?? mapVisibility(activeChannelAcl.channelPrivacySettings?.visibility);
      const nextVisibility = channelVisibilityDraft;

      if (previousVisibility !== nextVisibility) {
        await updateChannelVisibilityApi(programId, selectedCommunity, channelId, nextVisibility);
      }

      if (nextVisibility === 'private') {
        const desired = new Set(selectedChannelMembers.map((value) => value.toString()));
        if (channelCreatorId) {
          desired.add(channelCreatorId);
        }
        const original = new Set(channelMembersSnapshot.map((value) => value.toString()));

        const add = Array.from(desired).filter((id) => !original.has(id));
        const remove = Array.from(original).filter((id) => !desired.has(id) && (!channelCreatorId || id !== channelCreatorId));

        if (add.length > 0 || remove.length > 0) {
          await updateChannelMembersApi(programId, selectedCommunity, channelId, add, remove);
        }
      } else if (previousVisibility === 'private' && channelMembersSnapshot.length > 0) {
        const removable = channelMembersSnapshot
          .map((value) => value.toString())
          .filter((id) => !channelCreatorId || id !== channelCreatorId);
        if (removable.length > 0) {
          await updateChannelMembersApi(programId, selectedCommunity, channelId, [], removable);
        }
      }

      await ensureChannelAcl(true);
      await dispatch(fetchCommunityEnrollments(programId, selectedCommunity));
      handleCloseChannelAcl();
    } catch (err) {
      console.error('Failed to update channel ACL', err);
      setChannelAclError('Failed to update channel access');
    } finally {
      setChannelAclSaving(false);
    }
  }, [
    activeChannelAcl,
    channelAcl,
    channelCreatorId,
    channelMembersSnapshot,
    channelVisibilityDraft,
    dispatch,
    ensureChannelAcl,
    handleCloseChannelAcl,
    mapVisibility,
    programId,
    selectedChannelMembers,
    selectedCommunity,
    updateChannelMembersApi,
    updateChannelVisibilityApi,
  ]);

  useEffect(() => {
    if (viewMode === 'channels' && canManageChannelAcl) {
      (async () => {
        try {
          await ensureChannelAcl();
        } catch (err) {
          console.error('Failed to hydrate channel ACL data', err);
        }
      })();
    }
  }, [viewMode, canManageChannelAcl, ensureChannelAcl]);

  const handleToggleChannelVisibility = useCallback(
    async (channelId: string) => {
      if (!programId || !selectedCommunity || !canManageChannelAcl) return;
      const current = await getAclInfo(channelId);
      if (!current) return;
      const nextVisibility = current.visibility === 'private' ? 'public' : 'private';
      setVisibilityUpdating((prev) => ({ ...prev, [channelId]: true }));
      setChannelAclError(null);
      try {
        const response = await updateChannelVisibilityApi(
          programId,
          selectedCommunity,
          channelId,
          nextVisibility
        );
        applyChannelAclUpdate(channelId, response.channel);
      } catch (err) {
        console.error('Failed to update channel visibility', err);
        setChannelAclError('Failed to update channel visibility');
      } finally {
        setVisibilityUpdating((prev) => ({ ...prev, [channelId]: false }));
      }
    },
    [programId, selectedCommunity, canManageChannelAcl, getAclInfo, applyChannelAclUpdate]
  );

  const handleOpenMember = async (member: ICommunityRoleUser) => {
    setActiveMember(member);
    const draftRole = toDraftRole(member.role);
    setRoleDraft(draftRole === 'admin' ? '' : draftRole);

    // Initialize member details from user object
    const profile = member.user && typeof member.user === 'object' ? (member.user as any) : null;
    setFirstNameDraft(profile?.name || '');
    setLastNameDraft(profile?.lastName || '');
    setPhoneNumberDraft(profile?.phoneNumber || '');
    setEmailDraft(profile?.email || '');

    if (canManageChannelAcl) {
      try {
        await ensureChannelAcl();
      } catch (err) {
        console.error('Failed to refresh channel ACL', err);
      }
    }

    const normalizedUserId = member.userId ? member.userId.toString() : '';
    const visibleSet = new Set(normalizeVisibleChannelIds(member.visibleChannelIds || []));
    const overrides: Record<string, string> = {};

    for (const channel of channelOptions) {
      const normalizedChannelId = normalizeChannelKey(channel.channelIdentifier);
      const override = (member.moderatedChannels || []).find(
        (item) =>
          normalizeChannelKey(item?.channelIdentifier || '') === normalizedChannelId &&
          item.isActive !== false &&
          (!item.communityIdentifier || item.communityIdentifier === selectedCommunity)
      );
      let roleValue = (override?.role || '').toString().trim().toLowerCase();
      if (!roleValue && override) {
        roleValue = 'moderator';
      }
      const normalizedRole = ['admin', 'moderator', 'viewer'].includes(roleValue) ? roleValue : 'none';
      overrides[channel.channelIdentifier] = normalizedRole;

      let aclInfo = channelAcl[channel.channelIdentifier];
      if (!aclInfo && canManageChannelAcl) {
        try {
          aclInfo = await getAclInfo(channel.channelIdentifier);
        } catch (err) {
          console.error('Failed to load channel ACL', err);
        }
      }
      if (aclInfo?.visibility === 'private') {
        const allowedSet = new Set((aclInfo.allowedUserIds || []).map((value) => value?.toString?.() ?? value));
        const creatorId = aclInfo.createdByUserId ? aclInfo.createdByUserId.toString() : '';
        if (normalizedUserId && (allowedSet.has(normalizedUserId) || creatorId === normalizedUserId)) {
          visibleSet.add(channel.channelIdentifier);
        } else {
          visibleSet.delete(channel.channelIdentifier);
        }
      }
    }

    setOverrideDraft(overrides);
    setVisibleDraft(Array.from(visibleSet));
  };

  const handleCloseDialog = () => {
    if (saving) return;
    setActiveMember(null);
    setVisibleDraft([]);
    setOverrideDraft({});
    setRoleDraft('member');
    setFirstNameDraft('');
    setLastNameDraft('');
    setPhoneNumberDraft('');
    setEmailDraft('');
    setModeratorChannelMode(null);
    setShowChannelConfig(false);
    setChannelSearchTerm('');
  };

  const toggleVisibility = (channelIdentifier: string) => {
    setVisibleDraft((prev) => {
      const next = new Set(prev);
      if (next.has(channelIdentifier)) {
        next.delete(channelIdentifier);
      } else {
        next.add(channelIdentifier);
      }
      return Array.from(next.values());
    });
  };

  const updateOverride = (channelIdentifier: string, role: string) => {
    setOverrideDraft((prev) => ({
      ...prev,
      [channelIdentifier]: role,
    }));
  };

  const cycleOverride = (channelIdentifier: string) => {
    const current = overrideDraft[channelIdentifier] || 'none';
    const options = ['none', 'viewer', 'moderator', 'admin'];
    const currentIndex = options.indexOf(current);
    const nextIndex = (currentIndex + 1) % options.length;
    updateOverride(channelIdentifier, options[nextIndex]);
  };

  const handleAllChannels = () => {
    setModeratorChannelMode('all');
    const allModerated: Record<string, string> = {};
    channelOptions.forEach((channel: any) => {
      allModerated[channel.channelIdentifier] = 'moderator';
    });
    setOverrideDraft(allModerated);
    setShowChannelConfig(true);
  };

  const handleSelectChannels = () => {
    setModeratorChannelMode('select');
    setShowChannelConfig(true);
  };

  // Check if moderator has no channels assigned
  const hasNoModeratedChannels = useMemo(() => {
    return !Object.values(overrideDraft).some(role => role && role !== 'none');
  }, [overrideDraft]);

  const handleSave = async () => {
    if (!programId || !selectedCommunity || !activeMember) return;
    setSaving(true);
    try {
      // Update user details if changed
      const profile = activeMember.user && typeof activeMember.user === 'object' ? (activeMember.user as any) : null;
      const originalFirstName = profile?.name || '';
      const originalLastName = profile?.lastName || '';
      const originalPhoneNumber = profile?.phoneNumber || '';
      const originalEmail = profile?.email || '';

      const detailsChanged =
        firstNameDraft !== originalFirstName ||
        lastNameDraft !== originalLastName ||
        phoneNumberDraft !== originalPhoneNumber ||
        emailDraft !== originalEmail;

      if (detailsChanged) {
        try {
          const userId = activeMember.userId ? activeMember.userId.toString() : profile?._id;
          if (userId) {
            await saveUserApi({
              _id: userId,
              name: firstNameDraft,
              lastName: lastNameDraft,
              phoneNumber: phoneNumberDraft,
              email: emailDraft,
            });
          }
        } catch (err) {
          console.error('Failed to update user details', err);
          // Continue with other updates even if this fails
        }
      }

      const dedupedVisible = Array.from(new Set(visibleDraft));
      const normalizedUserId = (activeMember.userId || '').toString();
      const visibleSet = new Set(dedupedVisible);

      if (canManageChannelAcl && normalizedUserId) {
        setChannelAclError(null);
        for (const channel of channelOptions) {
          const channelId = channel?.channelIdentifier;
          if (!channelId) continue;

          const aclInfo = await getAclInfo(channelId);
          if (!aclInfo || aclInfo.visibility !== 'private') {
            continue;
          }

          const isCreator = aclInfo.createdByUserId === normalizedUserId;
          const userAllowed = aclInfo.allowedUserIds.includes(normalizedUserId);
          const shouldBeVisible = visibleSet.has(channelId);

          if (shouldBeVisible && !userAllowed) {
            try {
              const response = await updateChannelMembersApi(
                programId,
                selectedCommunity,
                channelId,
                [normalizedUserId],
                []
              );
              applyChannelAclUpdate(channelId, response.channel);
            } catch (err) {
              setChannelAclError('Failed to update channel access list');
              throw err;
            }
          } else if (!shouldBeVisible && userAllowed && !isCreator) {
            try {
              const response = await updateChannelMembersApi(
                programId,
                selectedCommunity,
                channelId,
                [],
                [normalizedUserId]
              );
              applyChannelAclUpdate(channelId, response.channel);
            } catch (err) {
              setChannelAclError('Failed to update channel access list');
              throw err;
            }
          }
        }
      }

      const visiblePayload = dedupedVisible.map((channelId) =>
        selectedCommunity ? `${selectedCommunity}:${channelId}` : channelId
      );
      const moderatedPayload = Object.entries(overrideDraft)
        .filter(([, role]) => role && role !== 'none')
        .map(([channelIdentifier, role]) => ({
          communityIdentifier: selectedCommunity,
          channelIdentifier,
          role,
          isActive: true,
        }));

      const desiredRole = fromDraftRole(roleDraft);
      const roleChanged = desiredRole && desiredRole !== activeMember.role ? desiredRole : undefined;

      await dispatch(
        updateCommunityMemberAccess({
          programId,
          userId: activeMember.userId,
          communityIdentifier: selectedCommunity,
          visibleChannelIds: visiblePayload,
          moderatedChannels: moderatedPayload,
          role: roleChanged,
        }) as any
      );

      setActiveMember(null);
      setVisibleDraft([]);
      setOverrideDraft({});
      setRoleDraft('member');
      setFirstNameDraft('');
      setLastNameDraft('');
      setPhoneNumberDraft('');
      setEmailDraft('');
      setChannelSearchTerm('');

      if (selectedCommunity) {
        history.push(`/admin/community/roles?community=${selectedCommunity}`);
      } else {
        history.push('/admin/community/roles');
      }
    } catch (err) {
      // reducer handles surfacing the error state
    } finally {
      setSaving(false);
    }
  };

  if (!canManageRoles) {
    if (!enrollment && !isAdminLike) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" style={themedStyles.container}>
          <CircularProgress style={{ color: '#007AFF' }} />
        </Box>
      );
    }

    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" style={themedStyles.container}>
        <Box style={{ ...themedStyles.memberCard, maxWidth: 480, textAlign: 'center' }}>
          <Typography style={{ color: '#666', fontSize: 15 }}>
            You do not have permission to manage community roles.
          </Typography>
        </Box>
      </Box>
    );
  }

  const renderRoleBadge = (role: string) => {
    const normalized = toDraftRole(role);
    const color = normalized === 'admin' ? '#0b5394' : normalized === 'moderator' ? '#b45f06' : '#386641';
    const bg = normalized === 'admin' ? '#d0e2ff' : normalized === 'moderator' ? '#fde7c6' : '#e6f4ea';
    return (
      <span
        style={{
          backgroundColor: bg,
          color,
          padding: '6px 12px',
          borderRadius: '999px',
          fontSize: 13,
          fontWeight: 600,
        }}
      >
        {formatRoleLabel(role)}
      </span>
    );
  };

  return (
    <div style={themedStyles.container}>
      <Box style={themedStyles.header}>
        <Box display="flex" alignItems="center" justifyContent="space-between" maxWidth="1200px" margin="0 auto">
          <Box display="flex" alignItems="center" style={{ gap: 16 }}>
            <IconButton
              onClick={() => {
                if (selectedCommunity) {
                  history.push(`/admin/channel/list?community=${selectedCommunity}`);
                } else {
                  history.push('/admin/channel/list');
                }
              }}
              style={{ padding: 8 }}
            >
              <ChevronLeftIcon style={{ color: secondaryForeground }} />
            </IconButton>
            <img
              src={communityLogoUrl || resolveHeaderUrl(appLogo)}
              alt="Logo"
              style={{ height: 32, width: 'auto' }}
            />
          </Box>

          <Box display="flex" alignItems="center" style={{ gap: 12 }}>
            {allPrograms && allPrograms.length > 1 && showCommunitySelector && (
              <select
                value={selectedProgram || ''}
                onChange={(e) => handleProgramChange(e.target.value)}
                style={themedStyles.select}
              >
                {allPrograms.map((program: any) => (
                  <option key={program._id} value={program._id}>
                    {program.name || program._id}
                  </option>
                ))}
              </select>
            )}

            {showCommunitySelector ? (
              <select
                value={selectedCommunity || ''}
                onChange={(e) => setSelectedCommunity(e.target.value)}
                style={themedStyles.select}
              >
                {communities.map((community) => (
                  <option key={community.communityIdentifier} value={community.communityIdentifier}>
                    {community.communityName || community.communityIdentifier}
                  </option>
                ))}
              </select>
            ) : (
              <Typography style={{ fontSize: 18, fontWeight: 600, color: secondaryForeground }}>
                {communityLabel}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      <Box style={themedStyles.mainContent}>
        <Box mb={4}>
          <Typography style={{ fontSize: 32, fontWeight: 600, color: secondaryForeground, marginBottom: 8 }}>
            Community Members
          </Typography>
          <Typography style={{ fontSize: 17, color: mutedTextColor }}>{communityLabel}</Typography>
        </Box>

        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
          <div style={themedStyles.toggleGroup}>
            {viewModes.map((mode) => {
              const isActive = viewMode === mode.value;
              return (
                <button
                  key={mode.value}
                  type="button"
                  onClick={() => setViewMode(mode.value)}
                  style={{
                    ...themedStyles.toggleButton,
                    backgroundColor: isActive ? '#ffffff' : 'transparent',
                    color: isActive ? accentColor : mutedTextColor,
                    boxShadow: isActive ? `0 1px 4px ${applyAlpha(secondaryForeground, 0.25)}` : 'none',
                  }}
                >
                  {mode.label}
                </button>
              );
            })}
          </div>
        </Box>

        {viewMode === 'members' ? (
          <>
            <Box mb={3}>
              <Box style={themedStyles.searchBox}>
                <SearchIcon style={{ marginRight: 12, color: mutedTextColor }} />
                <InputBase
                  placeholder="Search by name, email, or ID"
                  fullWidth
                  value={memberSearchTerm}
                  onChange={(e) => setMemberSearchTerm(e.target.value)}
                  style={{ fontSize: 15, color: secondaryForeground }}
                />
              </Box>
              <Box display="flex" flexWrap="wrap" style={{ gap: 12, marginTop: 12 }}>
                <Select
                  value={memberSearchField}
                  onChange={(e) => setMemberSearchField(e.target.value as 'both' | 'name' | 'email' | 'phone')}
                  variant="outlined"
                  style={{ ...themedStyles.select, minWidth: 160 }}
                >
                  <MenuItem value="both">Search name & email</MenuItem>
                  <MenuItem value="name">Search by name</MenuItem>
                  <MenuItem value="email">Search by email</MenuItem>
                  <MenuItem value="phone">Search by phone</MenuItem>
                </Select>
                <Select
                  value={memberRoleFilter}
                  onChange={(e) => setMemberRoleFilter(e.target.value as typeof memberRoleFilter)}
                  variant="outlined"
                  style={{ ...themedStyles.select, minWidth: 150 }}
                >
                  <MenuItem value="all">All roles</MenuItem>
                  <MenuItem value="member">Members</MenuItem>
                  <MenuItem value="moderator">Moderators</MenuItem>
                  <MenuItem value="communityadmin">Community Admins</MenuItem>
                  <MenuItem value="admin">Platform Admins</MenuItem>
                </Select>
                <Select
                  value={memberPhoneFilter}
                  onChange={(e) => setMemberPhoneFilter(e.target.value as typeof memberPhoneFilter)}
                  variant="outlined"
                  style={{ ...themedStyles.select, minWidth: 150 }}
                >
                  <MenuItem value="all">All members</MenuItem>
                  <MenuItem value="with-phone">With phone</MenuItem>
                  <MenuItem value="without-phone">Without phone</MenuItem>
                </Select>
                <Select
                  value={memberSort}
                  onChange={(e) => setMemberSort(e.target.value as 'name' | 'email')}
                  variant="outlined"
                  style={{ ...themedStyles.select, minWidth: 140 }}
                >
                  <MenuItem value="name">Sort by name</MenuItem>
                  <MenuItem value="email">Sort by email</MenuItem>
                </Select>
                <Select
                  value={memberSortDir}
                  onChange={(e) => setMemberSortDir(e.target.value as 'asc' | 'desc')}
                  variant="outlined"
                  style={{ ...themedStyles.select, minWidth: 120 }}
                >
                  <MenuItem value="asc">A → Z</MenuItem>
                  <MenuItem value="desc">Z → A</MenuItem>
                </Select>
              </Box>
            </Box>

            {error && (
              <Box mb={3} p={2} style={{ backgroundColor: '#fff5f5', border: '1px solid #fecaca', borderRadius: 12 }}>
                <Typography style={{ color: '#dc2626', fontSize: 14 }}>{error}</Typography>
              </Box>
            )}

            {loading ? (
              <Box display="flex" justifyContent="center" py={8}>
                <CircularProgress style={{ color: communityTheme.mainColor }} />
              </Box>
            ) : (
              <Box>
                {filteredMembers.map((member) => {
                  const profile = member.user && typeof member.user === 'object' ? member.user : null;
                  const displayName = profile?.name || profile?.email || member.userId;
                  const email = profile?.email || '';
                  const phoneNumber = profile?.phoneNumber || '';
                  const visibleCount = member.visibleChannelIds?.length || 0;
                  const moderatedCount = (member.moderatedChannels || []).filter((ch) => ch.isActive !== false).length;

                  return (
                    <Box
                      key={member._id || member.userId}
                      style={{
                        ...themedStyles.memberCard,
                        borderRadius: 18,
                      }}
                    >
                      <Box display="flex" alignItems="center" justifyContent="space-between" style={{ gap: 16 }}>
                        <Box flex={1}>
                          <Typography style={{ fontSize: 17, fontWeight: 600, color: secondaryForeground, marginBottom: 6 }}>
                            {displayName}
                          </Typography>
                          <Box display="flex" alignItems="center" style={{ gap: 16, marginBottom: 8 }}>
                            {renderRoleBadge(member.role || 'member')}
                            <Typography style={{ fontSize: 13, color: mutedTextColor }}>
                              Visible channels: <strong>{visibleCount}</strong>
                            </Typography>
                            <Typography style={{ fontSize: 13, color: mutedTextColor }}>
                              Overrides: <strong>{moderatedCount}</strong>
                            </Typography>
                          </Box>
                          <Box display="flex" alignItems="center" style={{ gap: 16 }}>
                            {email && (
                              <Typography style={{ fontSize: 13, color: mutedTextColor }}>
                                📧 {email}
                              </Typography>
                            )}
                            {phoneNumber && (
                              <Typography style={{ fontSize: 13, color: mutedTextColor }}>
                                📱 {phoneNumber}
                              </Typography>
                            )}
                            {!phoneNumber && (
                              <Typography style={{ fontSize: 13, color: applyAlpha(secondaryForeground, 0.4), fontStyle: 'italic' }}>
                                No phone
                              </Typography>
                            )}
                          </Box>
                        </Box>
                        <Button
                          onClick={() => handleOpenMember(member)}
                          style={{
                            ...themedStyles.button,
                            backgroundColor: accentColor,
                            color: accentForeground,
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = buttonHover)}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = accentColor)}
                        >
                          Manage
                        </Button>
                      </Box>
                    </Box>
                  );
                })}
                {filteredMembers.length === 0 && (
                  <Typography style={{ fontSize: 14, color: mutedTextColor }}>
                    No members match your filters.
                  </Typography>
                )}
              </Box>
            )}
          </>
        ) : (
          <Box style={themedStyles.memberCard} p={4} textAlign="center">
            <Typography style={{ fontSize: 18, fontWeight: 600, color: secondaryForeground, marginBottom: 12 }}>
              Pending Invitations
            </Typography>
            <Typography style={{ fontSize: 14, color: mutedTextColor, marginBottom: 24 }}>
              Invitation management UI coming soon. Backend API is fully functional.
            </Typography>
            <Typography style={{ fontSize: 13, color: mutedTextColor, fontFamily: 'monospace' }}>
              Available APIs: createInvitationApi, getInvitationsByCommunityApi, revokeInvitationApi, resendInvitationApi
            </Typography>
          </Box>
        )}
      </Box>

      <Dialog open={Boolean(activeMember)} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography style={{ fontSize: 20, fontWeight: 600, color: secondaryForeground }}>
            Manage Access
          </Typography>
          {activeMember?.user && (
              <Typography style={{ fontSize: 14, color: mutedTextColor, marginTop: 4 }}>
              {(activeMember.user as any)?.email || activeMember.userId}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent style={{ paddingTop: 16 }}>
          <Box mb={4}>
            <Typography style={{ fontSize: 14, fontWeight: 600, color: secondaryForeground, marginBottom: 12 }}>
              Member Details
            </Typography>
            <Box display="flex" style={{ gap: 12, marginBottom: 12 }}>
              <TextField
                label="First Name"
                value={firstNameDraft}
                onChange={(e) => setFirstNameDraft(e.target.value)}
                fullWidth
                variant="outlined"
                style={{ flex: 1 }}
              />
              <TextField
                label="Last Name"
                value={lastNameDraft}
                onChange={(e) => setLastNameDraft(e.target.value)}
                fullWidth
                variant="outlined"
                style={{ flex: 1 }}
              />
            </Box>
            <Box display="flex" style={{ gap: 12, marginBottom: 12 }}>
              <TextField
                label="Phone Number"
                value={phoneNumberDraft}
                onChange={(e) => setPhoneNumberDraft(e.target.value)}
                fullWidth
                variant="outlined"
                style={{ flex: 1 }}
              />
            </Box>
            <Box>
              <TextField
                label="Email"
                value={emailDraft}
                onChange={(e) => setEmailDraft(e.target.value)}
                fullWidth
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <WarningIcon style={{ color: '#f59e0b', fontSize: 20, marginLeft: 8 }} />
                  ),
                }}
              />
              <Box display="flex" alignItems="center" style={{ gap: 6, marginTop: 6 }}>
                <WarningIcon style={{ color: '#f59e0b', fontSize: 14 }} />
                <Typography style={{ fontSize: 12, color: '#f59e0b', fontWeight: 500 }}>
                  Caution: Changing email affects login credentials and communication
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box mb={4}>
            <Typography style={{ fontSize: 14, fontWeight: 600, color: secondaryForeground, marginBottom: 12 }}>
              Base Role
            </Typography>
            <Select
              value={roleDraft}
              onChange={(e) => setRoleDraft(e.target.value as string)}
              fullWidth
              variant="outlined"
              displayEmpty
              style={themedStyles.select}
            >
              <MenuItem value="" disabled>
                <em>— Not editable —</em>
              </MenuItem>
              {BASE_ROLE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Box>

          {roleDraft === 'moderator' && hasNoModeratedChannels && !showChannelConfig && (
            <Box mb={4} p={3} style={{ backgroundColor: subtleAccentSurface, borderRadius: 12, border: `1px solid ${borderSubtle}` }}>
              <Typography style={{ fontSize: 14, fontWeight: 600, color: secondaryForeground, marginBottom: 12 }}>
                Moderator Channel Access
              </Typography>
              <Typography style={{ fontSize: 13, color: mutedTextColor, marginBottom: 16 }}>
                Choose which channels this moderator can access:
              </Typography>
              <Box display="flex" style={{ gap: 12 }}>
                <Button
                  onClick={handleAllChannels}
                  variant="outlined"
                  style={{
                    ...themedStyles.button,
                    backgroundColor: '#ffffff',
                    color: accentColor,
                    borderColor: accentColor,
                    flex: 1,
                  }}
                >
                  All Channels
                </Button>
                <Button
                  onClick={handleSelectChannels}
                  variant="contained"
                  style={{
                    ...themedStyles.button,
                    backgroundColor: accentColor,
                    color: accentForeground,
                    flex: 1,
                  }}
                >
                  Select Channels
                </Button>
              </Box>
            </Box>
          )}

          {(showChannelConfig || roleDraft !== 'moderator' || !hasNoModeratedChannels) && (
            <Box>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography style={{ fontSize: 14, fontWeight: 600, color: secondaryForeground }}>
                  Channel Permissions
                </Typography>
                {roleDraft === 'moderator' && showChannelConfig && (
                  <Button
                    onClick={() => {
                      setShowChannelConfig(false);
                      setModeratorChannelMode(null);
                      setOverrideDraft({});
                    }}
                    style={{
                      ...themedStyles.button,
                      fontSize: 12,
                      padding: '6px 12px',
                      color: mutedTextColor,
                    }}
                  >
                    Change Selection
                  </Button>
                )}
              </Box>

            <Box style={themedStyles.channelSearchBox}>
              <SearchIcon style={{ marginRight: 10, color: mutedTextColor, fontSize: 16 }} />
              <InputBase
                placeholder="Search channels"
                fullWidth
                value={channelSearchTerm}
                onChange={(e) => setChannelSearchTerm(e.target.value)}
                style={{ fontSize: 14, color: secondaryForeground }}
              />
            </Box>

            {channelAclError && (
              <Typography style={{ fontSize: 12, color: '#b91c1c', marginBottom: 8 }}>
                {channelAclError}
              </Typography>
            )}
            {channelAclLoading && (
              <Typography style={{ fontSize: 12, color: mutedTextColor, marginBottom: 8 }}>
                Loading channel ACL info…
              </Typography>
            )}

            {filteredChannels.length === 0 ? (
              <Typography style={{ fontSize: 14, color: mutedTextColor, padding: 16, textAlign: 'center' }}>
                {channelSearchTerm ? 'No channels found' : 'No channels configured'}
              </Typography>
            ) : (
              <Box style={themedStyles.channelListContainer}>
                {filteredChannels.map((channel: any, index: number) => {
                  const channelId = channel.channelIdentifier;
                  const isVisible = visibleDraft.includes(channelId);
                  const aclInfo = channelAcl[channelId];
                  const isPrivateChannel = aclInfo?.visibility === 'private';
                  const isUpdatingVisibility = !!visibilityUpdating[channelId];
                  const normalizedUserId = activeMember?.userId ? activeMember.userId.toString() : '';
                  const userAllowed = normalizedUserId && aclInfo
                    ? aclInfo.allowedUserIds.includes(normalizedUserId)
                    : false;
                  const showAclHint = canManageChannelAcl && isPrivateChannel && !userAllowed;

                  const rowBackground = isVisible ? subtleAccentSurface : '#ffffff';
                  const rowBorder = index < filteredChannels.length - 1
                    ? `1px solid ${themedStyles.borderSubtle}`
                    : 'none';
                  const eyeBackground = isVisible ? mediumAccentSurface : surfaceSoft;
                  const eyeIconColor = isVisible ? accentColor : secondaryForeground;
                  const overrideValue = overrideDraft[channelId] || 'none';
                  const overrideActive = overrideValue !== 'none';
                  const overrideBackground = overrideActive ? applyAlpha(accentColor, 0.2) : surfaceSoft;
                  const overrideColor = overrideActive ? accentColor : secondaryForeground;
                  const lockBackground = isPrivateChannel ? applyAlpha(accentColor, 0.2) : surfaceSoft;
                  const lockIconColor = isPrivateChannel ? accentColor : secondaryForeground;

                  return (
                    <Box
                      key={channelId}
                      style={{
                        ...themedStyles.channelRow,
                        backgroundColor: rowBackground,
                        borderBottom: rowBorder,
                      }}
                    >
                      <Box display="flex" alignItems="center" style={{ gap: 12 }} flex={1}>
                        <Checkbox
                          color="primary"
                          checked={isVisible}
                          onChange={() => toggleVisibility(channelId)}
                          style={{
                            color: isVisible ? accentColor : mutedTextColor,
                          }}
                        />
                        <Box>
                          <Typography style={{ fontSize: 15, fontWeight: 500, color: secondaryForeground }}>
                            {channel.channelName || channelId}
                          </Typography>
                          {showAclHint && (
                            <Typography style={{ fontSize: 12, color: accentColor }}>
                              Saving will add this member to the allowed list.
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                        {canManageChannelAcl && (
                          <Box
                            style={{
                              ...themedStyles.lockButton,
                              backgroundColor: lockBackground,
                              color: lockIconColor,
                              cursor: 'default',
                            }}
                            title={isPrivateChannel ? 'Private channel (edit in Manage Channels)' : 'Public channel (edit in Manage Channels)'}
                          >
                            {isPrivateChannel ? (
                              <LockIcon style={{ fontSize: 18, color: lockIconColor }} />
                            ) : (
                              <LockOpenIcon style={{ fontSize: 18, color: lockIconColor }} />
                            )}
                          </Box>
                        )}

                        <button
                          onClick={() => cycleOverride(channelId)}
                          style={{
                            ...themedStyles.overrideBadge,
                            backgroundColor: overrideBackground,
                            color: overrideColor,
                          }}
                        >
                          <SecurityIcon style={{ fontSize: 14, color: overrideColor }} />
                          {overrideActive ? (
                            <span>
                              {overrideValue === 'viewer'
                                ? 'Viewer'
                                : overrideValue === 'moderator'
                                ? 'Mod'
                                : 'Admin'}
                            </span>
                          ) : (
                            <span style={{ opacity: 0.75 }}>
                              {roleDraft === 'member'
                                ? 'Member'
                                : roleDraft === 'moderator'
                                ? 'Mod'
                                : roleDraft === 'communityadmin'
                                ? 'Admin'
                                : 'Admin'}
                            </span>
                          )}
                        </button>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}

            <Typography style={{ fontSize: 12, color: mutedTextColor, marginTop: 12 }}>
              Use the checkbox to toggle channel visibility for this member. The lock icon indicates if the channel is public or private (manage in Channels). Use the shield badge to cycle channel overrides.
            </Typography>
          </Box>
          )}
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px' }}>
          <Button onClick={handleCloseDialog} disabled={saving} style={{ ...themedStyles.button, color: mutedTextColor }}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || Boolean(updatingUserId)}
            style={{
              ...themedStyles.button,
              backgroundColor: accentColor,
              color: accentForeground,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = buttonHover)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = accentColor)}
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={channelAclModalOpen} onClose={handleCloseChannelAcl} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography style={{ fontSize: 20, fontWeight: 600, color: secondaryForeground }}>
            Manage Channel Visibility
          </Typography>
          {activeChannelAcl && (
            <Typography style={{ fontSize: 14, color: mutedTextColor, marginTop: 4 }}>
              {activeChannelAcl.channelName || activeChannelAcl.channelIdentifier}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent style={{ paddingTop: 16 }}>
          {!activeChannelAcl ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress style={{ color: accentColor }} />
            </Box>
          ) : (
            <>
              <Box mb={4}>
                <Typography style={{ fontSize: 14, fontWeight: 600, color: secondaryForeground, marginBottom: 12 }}>
                  Visibility
                </Typography>
            <Select
              value={channelVisibilityDraft}
              onChange={(e) => setChannelVisibilityDraft(e.target.value as 'public' | 'private')}
              fullWidth
              variant="outlined"
              style={themedStyles.select}
            >
                  <MenuItem value="public">Public (all community members can see this channel)</MenuItem>
                  <MenuItem value="private">Private (only selected members can access)</MenuItem>
                </Select>
              </Box>

              {channelVisibilityDraft === 'private' ? (
                <Box>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography style={{ fontSize: 14, fontWeight: 600, color: secondaryForeground }}>
                      Allowed Members ({new Set(selectedChannelMembers).size - (channelCreatorId ? 1 : 0)} selected)
                    </Typography>
                    {channelCreatorId && (
                      <Typography style={{ fontSize: 12, color: mutedTextColor }}>
                        Channel creator always retains access.
                      </Typography>
                    )}
                  </Box>
                  <Box style={themedStyles.channelSearchBox}>
                    <SearchIcon style={{ marginRight: 10, color: mutedTextColor, fontSize: 16 }} />
                    <InputBase
                      placeholder="Search members"
                      fullWidth
                      value={channelMemberSearch}
                      onChange={(e) => setChannelMemberSearch(e.target.value)}
                      style={{ fontSize: 14, color: secondaryForeground }}
                    />
                  </Box>

                  <Box style={{ maxHeight: 300, overflowY: 'auto' }}>
                    {channelMemberOptions.map((option) => {
                      const isCreator = channelCreatorId === option.id;
                      const isSelected = selectedChannelMembers.includes(option.id);
                      return (
                        <Box
                          key={option.id}
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          style={{
                            padding: '10px 14px',
                            borderRadius: 12,
                            marginBottom: 8,
                            backgroundColor: isSelected ? mediumAccentSurface : surfaceSoft,
                            border: `1px solid ${isSelected ? mediumAccentSurface : borderSubtle}`,
                            opacity: isCreator ? 0.75 : 1,
                          }}
                        >
                          <Box>
                            <Typography style={{ fontSize: 14, fontWeight: 500, color: secondaryForeground }}>
                              {option.label}
                            </Typography>
                            <Typography style={{ fontSize: 12, color: mutedTextColor }}>
                              {option.email}
                            </Typography>
                          </Box>
                          <Box display="flex" alignItems="center" style={{ gap: 12 }}>
                            <Typography style={{ fontSize: 12, color: mutedTextColor }}>
                              {option.role}
                            </Typography>
                            {isCreator ? (
                              <Typography style={{ fontSize: 12, color: mutedTextColor }}>Creator</Typography>
                            ) : (
                              <Checkbox
                                color="primary"
                                checked={isSelected}
                                onChange={() => toggleChannelMemberSelection(option.id)}
                              />
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                    {channelMemberOptions.length === 0 && (
                      <Typography style={{ fontSize: 13, color: mutedTextColor }}>
                        No members match your search.
                      </Typography>
                    )}
                  </Box>
                </Box>
              ) : (
                <Box style={{ padding: '12px 16px', borderRadius: 12, backgroundColor: subtleAccentSurface }}>
                  <Typography style={{ fontSize: 13, color: secondaryForeground }}>
                    This channel is public. All community members can access it, so no additional member assignment is required.
                  </Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px' }}>
          <Button onClick={handleCloseChannelAcl} disabled={channelAclSaving} style={{ ...themedStyles.button, color: mutedTextColor }}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveChannelAcl}
            disabled={channelAclSaving}
            style={{
              ...themedStyles.button,
              backgroundColor: accentColor,
              color: accentForeground,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = buttonHover)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = accentColor)}
          >
            {channelAclSaving ? 'Saving…' : 'Save Settings'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AdminCommunityMembers;
